import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification } from "hooks/useNotification";
import {
  createTransportSedel,
  TTransportSedelSchema,
  transportSedelSchema,
  UppdragQueryKey,
} from "api/uppdrag.api";
import { Uppdrag } from "types/uppdrag.type";
import { useWasteStationList } from "api/wasteStation.api";
import { useVehicles } from "api/vehicle.api";
import { vehicleTypeToName } from "types/vehicle.type";
import { TransportSedel } from "types/transportSedel.type";
import { stringToDate } from "helpers/date";
import {
  patchTransportSedel,
  TransportSedelQueryKey,
} from "api/transportSedel.api";

export const useUpdateTransportSedelLogic = (
  uppdrag: Uppdrag,
  transportSedelToEdit: TransportSedel,
) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TTransportSedelSchema>({
    resolver: zodResolver(transportSedelSchema),
    defaultValues: {
      timeOfDepartureUtc: transportSedelToEdit ? undefined : new Date(),
      vehicleUuid: uppdrag.assignedToUser?.primaryVehicle?.uuid,
    },
  });

  useEffect(() => {
    if (transportSedelToEdit && isModalOpen) {
      form.reset({
        wasteStationUuid: transportSedelToEdit.wasteStation?.uuid,
        vehicleUuid: transportSedelToEdit.vehicle?.uuid,
        timeOfDepartureUtc: stringToDate(
          transportSedelToEdit.timeOfDepartureUtc,
        ),
      });
    }
  }, [form, transportSedelToEdit, isModalOpen]);

  // useEffect(() => {
  //   if (transportSedelToEdit) {
  //     resetForm();
  //   }
  // }, [resetForm, transportSedelToEdit]);

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
    // No form reset here - we'll do it when the modal reopens
  }, []);

  const afterModalClose = () =>
    form.reset({
      wasteStationUuid: transportSedelToEdit.wasteStation?.uuid,
      vehicleUuid: transportSedelToEdit.vehicle?.uuid,
      timeOfDepartureUtc: stringToDate(transportSedelToEdit.timeOfDepartureUtc),
    });

  const createMutation = useMutation({
    ...createTransportSedel(uppdrag.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      onModalClose();
    },
    onError: () => notification.error(),
  });

  const patchMutation = useMutation({
    ...patchTransportSedel(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      onModalClose();
    },
    onError: () => notification.error(),
  });

  const onSubmit = form.handleSubmit((data: TTransportSedelSchema) => {
    if (transportSedelToEdit) {
      patchMutation.mutate({
        id: transportSedelToEdit.id,
        payload: data,
      });
    } else {
      createMutation.mutate(data);
    }
  });

  // Wastestations
  const { data: wasteStations } = useWasteStationList();
  const wasteStationDropdownItems = useMemo(
    () =>
      (wasteStations ?? [])
        .map((x) => ({ label: x.name, value: x.uuid }))
        .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [wasteStations],
  );

  // Vehicles
  const { data: vehicles } = useVehicles();
  const vehicleDropdownItems = useMemo(
    () =>
      (vehicles ?? [])
        .map(({ uuid, registrationNumber, type }) => ({
          label: `${registrationNumber} - ${vehicleTypeToName(type)}`,
          value: uuid,
        }))
        .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [vehicles],
  );

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    form,
    onSubmit,
    mutation: createMutation,
    onModalClose,
    afterModalClose,
    uppdrag,
    wasteStationDropdownItems,
    vehicleDropdownItems,
  };
};

export type UpdateTransportSedelLogicRetVal = ReturnType<
  typeof useUpdateTransportSedelLogic
>;
